import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { batchStatusColors, BatchStatus } from '../stockManagementTypes';
import { listExpiringLots, markLotExpired, ExpiringLot } from '../../../../services/inventoryApi';
import { AsyncBoundary } from '../../../../components/AsyncBoundary';
import styles from './BatchExpiryTab.module.css';

const filters = ['All', 'Expired', 'Expiring Soon', 'Healthy'];

interface Row {
  lotId: number;
  batchId: string;
  sku: string;
  product: string;
  warehouse: string;
  qty: number;
  received: string;
  expiryDate: string;
  daysLeft: number;
  fefo: string;
  status: BatchStatus;
}

function statusFor(daysLeft: number, qty: number): BatchStatus {
  if (qty <= 0 || daysLeft < 0) return 'Expiring';   // already past / out of stock
  if (daysLeft < 7) return 'Exp Soon';
  return 'Healthy';
}

function adapt(lot: ExpiringLot): Row {
  return {
    lotId: lot.lot_id,
    batchId: lot.lot_number,
    sku: lot.sku_code,
    product: lot.product_name || lot.sku_code,
    warehouse: lot.location_code || 'N/A',
    qty: lot.quantity_on_hand ?? 0,
    received: lot.received_date ? new Date(lot.received_date).toLocaleDateString() : 'N/A',
    expiryDate: new Date(lot.expiration_date).toLocaleDateString(),
    daysLeft: lot.days_until_expiration,
    fefo: 'FIFO',
    status: statusFor(lot.days_until_expiration, lot.quantity_on_hand ?? 0),
  };
}

export const BatchExpiryTab: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyLotId, setBusyLotId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const lots = await listExpiringLots();
      setRows(lots.map(adapt));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load expiring lots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const expiredCount = rows.filter((b) => b.status === 'Expiring').length;
  const expSoonCount = rows.filter((b) => b.status === 'Exp Soon').length;

  const filteredItems = useMemo(() => {
    let items = rows;
    if (activeFilter === 'Expired') items = items.filter((b) => b.status === 'Expiring');
    else if (activeFilter === 'Expiring Soon') items = items.filter((b) => b.status === 'Exp Soon');
    else if (activeFilter === 'Healthy') items = items.filter((b) => b.status === 'Healthy');
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((b) =>
        b.batchId.toLowerCase().includes(q)
        || b.product.toLowerCase().includes(q)
        || b.sku.toLowerCase().includes(q)
      );
    }
    return items;
  }, [activeFilter, search, rows]);

  const handleMarkExpired = async (lotId: number, label: string) => {
    if (!window.confirm(`Mark batch ${label} as expired? This zeros its on-hand stock.`)) return;
    setBusyLotId(lotId);
    try {
      await markLotExpired(lotId);
      // Optimistically remove from the list (server-side filter excludes
      // quantity_on_hand=0 lots, so a refetch would drop it too).
      setRows((prev) => prev.filter((r) => r.lotId !== lotId));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to mark expired');
    } finally {
      setBusyLotId(null);
    }
  };

  return (
    <div>
      <div className={styles.subHeader}>
        <p className={styles.subtitle}>Inventory monitoring — detect, investigate & correct in real time</p>
        <div className={styles.subActions}>
          <button
            type="button"
            className={styles.fullInvBtn}
            onClick={fetchData}
            disabled={loading}
            title="Refresh expiring lots from the server"
          >
            {loading ? 'Refreshing…' : 'Full Inventory'}
          </button>
          {/* Bulk-mark-expired isn't wired (would need multi-select). Per-row
              "Mark Expired" in the ACTIONS column is the actionable path. */}
          <button
            type="button"
            className={styles.markBtn}
            disabled
            title="Use the per-row Mark Expired action in the ACTIONS column"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Mark expired
          </button>
        </div>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          {filters.map((f) => {
            let label = f;
            if (f === 'Expired') label = `Expired (${expiredCount})`;
            if (f === 'Expiring Soon') label = `Expiring Soon (${expSoonCount})`;
            const isActive = activeFilter === f;
            return (
              <button
                key={f}
                className={`${styles.pill} ${isActive ? styles.pillActive : ''} ${!isActive && f === 'Expired' ? styles.pillRed : ''} ${!isActive && f === 'Expiring Soon' ? styles.pillOrange : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className={styles.searchBox}>
          <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input className={styles.searchInput} placeholder="Search batch..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <AsyncBoundary
        loading={loading && rows.length === 0}
        error={error}
        empty={!loading && filteredItems.length === 0}
        emptyMessage="No batches matching the filter"
        onRetry={fetchData}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>BATCH ID</th>
                <th>SKU</th>
                <th>PRODUCT</th>
                <th>WAREHOUSE</th>
                <th>QTY</th>
                <th>RECEIVED</th>
                <th>EXPIRY DATE</th>
                <th>DAYS LEFT</th>
                <th>FEFO</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.lotId}>
                  <td className={styles.idCell}>{item.batchId}</td>
                  <td className={styles.skuCell}>{item.sku}</td>
                  <td>{item.product}</td>
                  <td>{item.warehouse}</td>
                  <td className={styles.bold}>{item.qty}</td>
                  <td>{item.received}</td>
                  <td className={styles.bold}>{item.expiryDate}</td>
                  <td className={styles.bold}>{item.daysLeft}</td>
                  <td>{item.fefo}</td>
                  <td>
                    <span className={styles.statusBadge} style={{ color: batchStatusColors[item.status] }}>
                      <span className={styles.statusDot} style={{ background: batchStatusColors[item.status] }} />
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${item.status === 'Expiring' ? styles.actionBtnRed : ''}`}
                      onClick={() => handleMarkExpired(item.lotId, item.batchId)}
                      disabled={busyLotId === item.lotId}
                    >
                      {busyLotId === item.lotId ? 'Working…' : 'Mark Expired'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>
    </div>
  );
};
